import { forwardRef, useState, type ComponentProps } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "../../lib/utils";
import { RadioGroupButtonsContext, type RadioGroupButtonVariant } from "./radioGroupButtonsContext";

export interface RadioGroupButtonsProps extends ComponentProps<typeof RadioGroupPrimitive.Root> {
  selectedVariant?: RadioGroupButtonVariant;
  unselectedVariant?: RadioGroupButtonVariant;
}

export const RadioGroupButtons = forwardRef<HTMLDivElement, RadioGroupButtonsProps>(
  (
    {
      className,
      selectedVariant = "secondary",
      unselectedVariant = "ghost",
      value: controlledValue,
      defaultValue,
      onValueChange,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const currentValue = controlledValue ?? uncontrolledValue;

    const handleValueChange = (newValue: string) => {
      if (controlledValue === undefined) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
    };

    return (
      <RadioGroupButtonsContext.Provider value={{ value: currentValue, selectedVariant, unselectedVariant }}>
        <RadioGroupPrimitive.Root
          ref={ref}
          data-slot="radio-group-buttons"
          className={cn("flex gap-1", className)}
          value={controlledValue}
          defaultValue={defaultValue}
          onValueChange={handleValueChange}
          {...props}
        />
      </RadioGroupButtonsContext.Provider>
    );
  }
);
RadioGroupButtons.displayName = "RadioGroupButtons";
